import React, { useCallback, useEffect, useState } from 'react';
import api, { formatApiError } from '@/lib/api';
import { Loader2, Plus, RefreshCw, Ticket, Ban, Percent } from 'lucide-react';

/**
 * Surgical Suite offline entitlement — coupon minting & audit trail.
 * Doctors scan the platform UPI QR (vivektikoo@okhdfcbank) and pay ₹5,000/month.
 * After receiving the payment snapshot on WhatsApp/GPay, the founder mints a
 * per-doctor coupon here with a % discount and tenure days + monthly cap.
 */
export default function SurgicalCouponsTab() {
  const [rows, setRows] = useState([]);
  const [redemptions, setRedemptions] = useState([]);
  const [entitlements, setEntitlements] = useState([]);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState('');

  const [code, setCode] = useState('');
  const [discount, setDiscount] = useState(100);
  const [days, setDays] = useState(30);
  const [cap, setCap] = useState(1000);
  const [maxUses, setMaxUses] = useState(1);
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [notes, setNotes] = useState('');
  const [creating, setCreating] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    setBusy(true); setErr('');
    try {
      const { data } = await api.get('/admin/surgical/coupons');
      setRows(data.rows || []); setRedemptions(data.redemptions || []); setEntitlements(data.entitlements || []);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || 'Failed to load'); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setCreating(true); setMsg('');
    try {
      await api.post('/admin/surgical/coupons', {
        code: code.trim().toUpperCase(),
        discount_percent: Number(discount) || 100,
        tenure_days: Number(days) || 30,
        max_discharges_per_period: Number(cap) || 1000,
        max_uses: Number(maxUses) || 1,
        restricted_to_email: email.trim() || null,
        restricted_to_mobile: mobile.trim() || null,
        notes: notes.trim() || null,
      });
      setMsg(`Minted: ${code.trim().toUpperCase()}`);
      setCode(''); setEmail(''); setMobile(''); setNotes('');
      load();
      setTimeout(() => setMsg(''), 4000);
    } catch (e2) { setMsg('Error: ' + (formatApiError(e2.response?.data?.detail) || 'Failed')); }
    finally { setCreating(false); }
  };

  const revoke = async (c) => {
    if (!window.confirm(`Revoke ${c}? Doctors currently on it stay active until expiry.`)) return;
    try { await api.post(`/admin/surgical/coupons/${c}/revoke`); load(); }
    catch (e) { alert(formatApiError(e.response?.data?.detail) || 'Failed'); }
  };

  if (busy) return <div className="text-center py-10"><Loader2 className="animate-spin text-teal-600 mx-auto" /></div>;

  return (
    <div className="space-y-6" data-testid="surgical-coupons-tab">
      {err && <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-xs text-rose-700">{err}</div>}

      <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="surg-mint-card">
        <div className="flex items-center gap-2 mb-1">
          <Ticket size={18} className="text-teal-600" />
          <h3 className="text-base font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Mint Surgical Suite Access Code</h3>
        </div>
        <p className="text-xs text-slate-500 mb-4">
          Product: <span className="font-semibold">Surgical Suite (IPD discharge)</span> · List price ₹5,000/month · up to 1,000 discharges/month · no carry-over.
          Doctor pays via UPI to <span className="font-mono">vivektikoo@okhdfcbank</span>, sends payment snapshot, then redeems this code inside <span className="font-mono">/surgical-suite</span>.
        </p>
        <form onSubmit={handleCreate} className="grid md:grid-cols-3 gap-3">
          <div>
            <label className="text-[11px] font-medium text-slate-600 mb-1 block">Code</label>
            <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
                   placeholder="e.g. RAINA-30D-100" required minLength={4} maxLength={40}
                   className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm font-mono tracking-wider outline-none focus:border-teal-400"
                   data-testid="mint-code-input" />
          </div>
          <div>
            <label className="text-[11px] font-medium text-slate-600 mb-1 block">Discount %</label>
            <input type="number" min={1} max={100} value={discount} onChange={(e) => setDiscount(e.target.value)}
                   className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-teal-400"
                   data-testid="mint-discount-input" />
          </div>
          <div>
            <label className="text-[11px] font-medium text-slate-600 mb-1 block">Tenure (days)</label>
            <input type="number" min={1} max={730} value={days} onChange={(e) => setDays(e.target.value)}
                   className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-teal-400"
                   data-testid="mint-days-input" />
          </div>
          <div>
            <label className="text-[11px] font-medium text-slate-600 mb-1 block">Monthly discharge cap</label>
            <input type="number" min={1} max={100000} value={cap} onChange={(e) => setCap(e.target.value)}
                   className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-teal-400"
                   data-testid="mint-cap-input" />
          </div>
          <div>
            <label className="text-[11px] font-medium text-slate-600 mb-1 block">Max uses</label>
            <input type="number" min={1} max={1000} value={maxUses} onChange={(e) => setMaxUses(e.target.value)}
                   className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-teal-400"
                   data-testid="mint-uses-input" />
          </div>
          <div>
            <label className="text-[11px] font-medium text-slate-600 mb-1 block">Restrict to email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                   placeholder="e.g. doctor@hospital.com"
                   className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-teal-400"
                   data-testid="mint-email-input" />
          </div>
          <div className="md:col-span-2">
            <label className="text-[11px] font-medium text-slate-600 mb-1 block">Restrict to mobile (optional)</label>
            <input value={mobile} onChange={(e) => setMobile(e.target.value)}
                   placeholder="e.g. 9418002211"
                   className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-teal-400"
                   data-testid="mint-mobile-input" />
          </div>
          <div>
            <label className="text-[11px] font-medium text-slate-600 mb-1 block">Notes (internal)</label>
            <input value={notes} onChange={(e) => setNotes(e.target.value)}
                   placeholder="Reason / txn ref"
                   className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-teal-400"
                   data-testid="mint-notes-input" />
          </div>
          <div className="md:col-span-3 flex items-center gap-3">
            <button type="submit" disabled={creating}
                    className="px-5 py-2 rounded-lg bg-teal-600 text-white text-sm font-semibold hover:bg-teal-700 disabled:opacity-50 flex items-center gap-2"
                    data-testid="mint-submit">
              {creating ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              Mint Access Code
            </button>
            {msg && <span className={`text-xs font-medium ${msg.startsWith('Error') ? 'text-rose-600' : 'text-emerald-600'}`}>{msg}</span>}
          </div>
        </form>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Percent size={16} className="text-teal-600" />
            <h3 className="text-base font-semibold text-slate-900">Access Codes ({rows.length})</h3>
          </div>
          <button onClick={load} className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"><RefreshCw size={12} /> Refresh</button>
        </div>
        {rows.length === 0 ? <p className="text-xs text-slate-500">No surgical codes minted yet.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="text-left text-slate-500 border-b border-slate-200">
                <th className="py-2 px-2">Code</th><th className="py-2 px-2">Discount</th><th className="py-2 px-2">Tenure</th>
                <th className="py-2 px-2">Cap/mo</th><th className="py-2 px-2">Uses</th>
                <th className="py-2 px-2">Restricted to</th><th className="py-2 px-2">Created</th><th className="py-2 px-2"></th>
              </tr></thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.code} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2 px-2 font-mono tracking-wider">{c.code}</td>
                    <td className="py-2 px-2">{c.discount_percent}%</td>
                    <td className="py-2 px-2">{c.tenure_days}d</td>
                    <td className="py-2 px-2">{c.max_discharges_per_period}</td>
                    <td className="py-2 px-2">{c.used_count || 0}/{c.max_uses}</td>
                    <td className="py-2 px-2 truncate max-w-[200px]">{c.restricted_to_email || c.restricted_to_mobile || '—'}</td>
                    <td className="py-2 px-2 text-slate-500">{(c.created_at || '').slice(0, 10)}</td>
                    <td className="py-2 px-2 text-right">
                      {c.active !== false && (
                        <button onClick={() => revoke(c.code)} className="text-rose-600 hover:text-rose-700 inline-flex items-center gap-1" data-testid={`revoke-${c.code}`}>
                          <Ban size={12} /> Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="text-base font-semibold text-slate-900 mb-3">Active Entitlements ({entitlements.length})</h3>
        {entitlements.length === 0 ? <p className="text-xs text-slate-500">No doctor is currently entitled.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="text-left text-slate-500 border-b border-slate-200">
                <th className="py-2 px-2">Doctor</th><th className="py-2 px-2">Code</th><th className="py-2 px-2">Expires</th>
                <th className="py-2 px-2">Used/Cap</th><th className="py-2 px-2">Lifetime</th>
              </tr></thead>
              <tbody>
                {entitlements.map((e) => (
                  <tr key={e.doctor_id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2 px-2">{e.doctor_name} <span className="text-slate-500">({e.doctor_email})</span></td>
                    <td className="py-2 px-2 font-mono">{e.coupon_code}</td>
                    <td className="py-2 px-2">{(e.expires_at || '').slice(0, 16).replace('T', ' ')}</td>
                    <td className="py-2 px-2">{e.discharges_used_in_period || 0} / {e.max_discharges_per_period}</td>
                    <td className="py-2 px-2 text-slate-500">{e.discharges_used_lifetime || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="text-base font-semibold text-slate-900 mb-3">Recent Redemptions ({redemptions.length})</h3>
        {redemptions.length === 0 ? <p className="text-xs text-slate-500">No redemptions yet.</p> : (
          <ul className="text-xs space-y-1">
            {redemptions.slice(0, 20).map((r, i) => (
              <li key={i} className="flex justify-between border-b border-slate-100 py-1">
                <span><span className="font-mono">{r.code}</span> · {r.doctor_email} · {r.tenure_days}d · cap {r.monthly_cap}</span>
                <span className="text-slate-500">{(r.ts || '').slice(0, 16).replace('T', ' ')}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
